import { requireCompletedProfile } from "@hisani/auth";
import { buildVaccinationScanFields, missingCompulsoryVaccinationScanFields, } from "../domain/extraction/vaccination-scan-extraction.js";
import { beginVaccinationScanUploadSchema, scanVaccinationDocumentSchema, stableExtractionUnavailable, stableForbidden, stableUploadUnavailable, } from "../validation.js";
import { requireFeatureMembership, requireHorseAcceptingSealedRecords, } from "./sealed-record-support.js";
/** Presigned PUT window a client has to finish a vaccination-stamp photo
 * upload — mirrors `HORSE_SCAN_UPLOAD_EXPIRY_SECONDS`
 * (`horse-scan-use-cases.ts`). */
const VACCINATION_SCAN_UPLOAD_EXPIRY_SECONDS = 300;
function objectKeyPrefix(stableId) {
    return `private/stable/${stableId}/vaccination-scans/`;
}
function safeFilename(filename) {
    return filename.replace(/[^A-Za-z0-9._-]/g, "-");
}
/**
 * Begins a presigned upload for a stamped-passport-page photo
 * (`docs/products/stable.md` "Vaccinations": "a vet photographs the stamped
 * passport page and uploads it"). Gated on Vaccinations USE access (the same
 * Entity Roles that can log a dose) rather than Horse Details' editor gate —
 * unlike the horse-add scan, this always targets an existing horse.
 */
export async function beginVaccinationScanUpload(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = beginVaccinationScanUploadSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, "VACCINATIONS", "USE");
    await requireHorseAcceptingSealedRecords(dependencies, parsed.stableId, parsed.horseId);
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const objectKey = `${objectKeyPrefix(parsed.stableId)}${parsed.id}-${safeFilename(parsed.filename)}`;
    const { uploadUrl } = await uploads.createUpload({
        objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
        expiresInSeconds: VACCINATION_SCAN_UPLOAD_EXPIRY_SECONDS,
    });
    return { id: parsed.id, objectKey, uploadUrl };
}
/**
 * Verifies the uploaded photo, runs the shared extraction port against it
 * (synchronously, in this same request — re-evaluating ADR 0015's decision
 * for this second caller and reaching the same conclusion: one small,
 * interactive, bounded-volume photo scan, see the ADR addendum), and
 * returns a confirm-screen draft. Never saves a dose itself — the client
 * edits the draft into the ordinary create/log-outcome form and submits
 * through the same validated path manual entry uses, exactly like the
 * horse-add scan (`scanHorseDocument`).
 */
export async function scanVaccinationDocument(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = scanVaccinationDocumentSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, "VACCINATIONS", "USE");
    await requireHorseAcceptingSealedRecords(dependencies, parsed.stableId, parsed.horseId);
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const prefix = objectKeyPrefix(parsed.stableId);
    if (!parsed.objectKey.startsWith(`${prefix}${parsed.id}-`)) {
        throw stableForbidden();
    }
    const uploaded = await uploads.assertUploaded({
        objectKey: parsed.objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
    });
    await uploads.claimUploaded({
        objectKey: parsed.objectKey,
        versionId: uploaded.versionId,
    });
    const extraction = dependencies.documentExtraction;
    if (extraction === undefined)
        throw stableExtractionUnavailable();
    const result = await extraction.extract({
        kind: parsed.kind,
        objectKey: parsed.objectKey,
        contentType: parsed.contentType,
    });
    const policy = await dependencies.policy.get();
    const fields = buildVaccinationScanFields(result.fields, new Set(Object.keys(policy.vaccineTypes)));
    const missingCompulsoryFields = missingCompulsoryVaccinationScanFields(fields);
    const now = dependencies.clock.now();
    const extractionId = dependencies.newId();
    await dependencies.repository.createVaccinationScanExtraction({
        id: extractionId,
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        sourceObjectKey: parsed.objectKey,
        provider: result.provider,
        modelId: result.modelId,
        modelVersion: result.modelVersion,
        fields,
        actorId: actor.account.id,
        now,
    });
    return {
        extractionId,
        fields,
        missingCompulsoryFields,
        provider: result.provider,
        modelId: result.modelId,
        modelVersion: result.modelVersion,
        extractedAt: result.extractedAt,
    };
}
